import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';
import { API_URL, resolveMediaUrl, api } from '../utils/api';
import OfflineBanner from './OfflineBanner';

function NavLinks({ t }) {
  return [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.salons'), href: '/shops' },
    { label: t('nav.products'), href: '/products' },
  ];
}

function getFirstName(name) {
  if (!name || typeof name !== 'string') return '';
  const trimmedName = name.trim();
  if (!trimmedName) return '';
  return trimmedName.split(/\s+/)[0] || '';
}

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
      <button
        type="button"
        onClick={() => i18n.changeLanguage('en')}
        style={{
          fontFamily: "'Oswald', sans-serif",
          fontSize: '0.75rem',
          fontWeight: isAr ? 400 : 700,
          letterSpacing: '0.08em',
          padding: '0.35rem 0.6rem',
          border: '1px solid var(--border)',
          borderRadius: '4px',
          background: isAr ? 'transparent' : 'var(--orange)',
          color: isAr ? '#ffffff' : '#fff',
          cursor: 'pointer',
        }}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => i18n.changeLanguage('ar')}
        style={{
          fontFamily: "'Oswald', sans-serif",
          fontSize: '0.75rem',
          fontWeight: isAr ? 700 : 400,
          letterSpacing: '0.08em',
          padding: '0.35rem 0.6rem',
          border: '1px solid var(--border)',
          borderRadius: '4px',
          background: isAr ? 'var(--orange)' : 'transparent',
          color: isAr ? '#fff' : '#ffffff',
          cursor: 'pointer',
        }}
      >
        AR
      </button>
    </div>
  );
}

const ORANGE_VARS = {
  '--orange': '#0064b2',
  '--orange-light': '#3399e6',
  '--orange-dark': '#004d8c',
  '--border': 'rgba(0,100,178,0.18)',
  '--border-hover': 'rgba(0,100,178,0.45)',
  '--orange-glow': 'rgba(0,100,178,0.25)',
  '--shadow-orange': '0 4px 24px rgba(0,100,178,0.3)',
  '--texture-color': 'rgba(0,100,178,0.6)',
  '--texture-color-2': 'rgba(0,100,178,0.4)',
  '--card-hover-ring': 'rgba(0,100,178,0.1)',
  '--focus-ring': 'rgba(0,100,178,0.12)',
  '--input-bg': 'rgba(0,100,178,0.05)',
  '--bg-base': '#000000',
  '--bg-dark': '#0a0a0a',
  '--bg-card': '#0c0c0c',
  '--bg-card-hover': '#111111',
  '--bg-footer': '#000000',
  '--nav-bg': 'rgba(0,0,0,0.85)',
  '--nav-bg-scrolled': 'rgba(0,0,0,0.97)',
  '--text-primary': '#ffffff',
  '--text-muted': '#9ca3af',
  '--text-warm': '#ffffff',
};
const PURPLE_VARS = {
  '--orange': '#ff00aa',
  '--orange-light': '#ff4dc4',
  '--orange-dark': '#cc0088',
  '--border': 'rgba(255,0,170,0.18)',
  '--border-hover': 'rgba(255,0,170,0.45)',
  '--orange-glow': 'rgba(255,0,170,0.25)',
  '--shadow-orange': '0 4px 24px rgba(255,0,170,0.3)',
  '--texture-color': 'rgba(255,0,170,0.6)',
  '--texture-color-2': 'rgba(255,0,170,0.4)',
  '--card-hover-ring': 'rgba(255,0,170,0.1)',
  '--focus-ring': 'rgba(255,0,170,0.12)',
  '--input-bg': 'rgba(255,0,170,0.05)',
  '--bg-base': '#000000',
  '--bg-dark': '#0a0a0a',
  '--bg-card': '#0c0c0c',
  '--bg-card-hover': '#111111',
  '--bg-footer': '#000000',
  '--nav-bg': 'rgba(0,0,0,0.85)',
  '--nav-bg-scrolled': 'rgba(0,0,0,0.97)',
  '--text-primary': '#ffffff',
  '--text-muted': '#ffb3e6',
  '--text-warm': '#ffccf0',
};

const PREMIUM_GOLD_VARS = {
  '--orange': '#D4AF37',
  '--orange-light': '#E5C76B',
  '--orange-dark': '#A88B25',
  '--border': 'rgba(212,175,55,0.2)',
  '--border-hover': 'rgba(212,175,55,0.45)',
  '--orange-glow': 'rgba(212,175,55,0.25)',
  '--shadow-orange': '0 4px 24px rgba(212,175,55,0.3)',
  '--texture-color': 'rgba(212,175,55,0.6)',
  '--texture-color-2': 'rgba(212,175,55,0.4)',
  '--card-hover-ring': 'rgba(212,175,55,0.1)',
  '--focus-ring': 'rgba(212,175,55,0.12)',
  '--input-bg': 'rgba(212,175,55,0.05)',
  '--bg-base': '#050505',
  '--bg-dark': '#0a0a0a',
  '--bg-card': '#111111',
  '--bg-card-hover': '#1a1a1a',
  '--bg-footer': '#050505',
  '--nav-bg': 'rgba(5,5,5,0.9)',
  '--nav-bg-scrolled': 'rgba(5,5,5,0.97)',
  '--text-primary': '#ffffff',
  '--text-muted': '#B8A47C',
  '--text-warm': '#E5C76B',
};

const DASHBOARD_LIGHT_VARS = {
  '--orange': '#0ea5e9',
  '--orange-light': '#38bdf8',
  '--orange-dark': '#0284c7',
  '--border': '#e2e8f0',
  '--border-hover': '#cbd5e1',
  '--orange-glow': 'rgba(14,165,233,0.15)',
  '--shadow-orange': '0 4px 24px rgba(14,165,233,0.2)',
  '--texture-color': 'rgba(14,165,233,0.08)',
  '--texture-color-2': 'rgba(14,165,233,0.05)',
  '--card-hover-ring': 'rgba(14,165,233,0.06)',
  '--focus-ring': 'rgba(14,165,233,0.12)',
  '--input-bg': '#ffffff',
  '--bg-base': '#f4f6f8',
  '--bg-dark': '#e2e8f0',
  '--bg-card': '#ffffff',
  '--bg-card-hover': '#f8fafc',
  '--bg-footer': '#f4f6f8',
  '--nav-bg': '#ffffff',
  '--nav-bg-scrolled': '#ffffff',
  '--text-primary': '#334155',
  '--text-muted': '#64748b',
  '--text-warm': '#0ea5e9',
};

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { signupTheme } = useTheme();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [vipStatus, setVipStatus] = useState(null);
  const navLinks = NavLinks({ t });
  const firstName = getFirstName(user?.name);

  const useFemaleTheme = user ? user.gender === 'female' : signupTheme === 'female';
  const isDashboardRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/manager');
  const isPremiumRoute = location.pathname.startsWith('/premium');

  useEffect(() => {
    const root = document.documentElement;
    let vars;
    if (isDashboardRoute) {
      vars = DASHBOARD_LIGHT_VARS;
    } else if (isPremiumRoute) {
      vars = PREMIUM_GOLD_VARS;
    } else if (useFemaleTheme) {
      vars = PURPLE_VARS;
    } else {
      vars = ORANGE_VARS;
    }
    Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
  }, [user?.gender, signupTheme, useFemaleTheme, isDashboardRoute, isPremiumRoute]);

  useEffect(() => {
    if (!user) { setVipStatus(null); return; }
    api.getMyMemberships()
      .then(mems => {
        if (mems.some(m => m.status === 'active')) setVipStatus('active');
        else if (mems.some(m => m.status === 'pending_approval')) setVipStatus('pending');
        else setVipStatus('none');
      })
      .catch(() => setVipStatus(null));
  }, [user?.id]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/'); };

  if (isDashboardRoute) {
    return (
      <div style={{ minHeight: '100vh', fontFamily: "'Roboto', sans-serif", background: 'var(--bg-base)' }}>
        <OfflineBanner />
        {children}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', fontFamily: "'Roboto', sans-serif", background: 'var(--bg-base)' }}>
      <OfflineBanner />

      {/* ── Top orange bar ── */}
      <div className="orange-bar" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 101, height: '4px' }} />

      {/* ── Navbar ── */}
      <nav style={{
        position: 'fixed',
        top: '4px',
        width: '100%',
        zIndex: 100,
        background: scrolled ? 'var(--nav-bg-scrolled)' : 'var(--nav-bg)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: scrolled ? '1px solid var(--border-hover)' : '1px solid var(--border)',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem', height: '4.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <img
              src="/Logo-Yalla-Salon.png"
              alt="Yalla Salon"
              style={{
                height: '52px',
                width: 'auto',
                mixBlendMode: 'screen',
                transition: 'transform 0.25s ease, opacity 0.25s ease',
              }}
              onMouseEnter={e => { e.target.style.transform = 'scale(1.05)'; e.target.style.opacity = '0.85'; }}
              onMouseLeave={e => { e.target.style.transform = 'scale(1)'; e.target.style.opacity = '1'; }}
            />
          </Link>

          {/* Mobile menu toggle */}
          <button
            className="mobile-nav-toggle"
            style={{ display: 'none', background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-menu"
          >
            {mobileOpen ? '\u2715' : '\u2630'}
          </button>

          {/* Center nav links */}
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '1.75rem' }}>
            {navLinks.map(link => (
              <Link
                key={link.label}
                to={link.href}
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: location.pathname === link.href ? 'var(--orange)' : '#ffffff',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  position: 'relative',
                }}
                onMouseEnter={e => e.target.style.color = 'var(--orange)'}
                onMouseLeave={e => e.target.style.color = location.pathname === link.href ? 'var(--orange)' : '#ffffff'}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right: lang switcher + cart + auth */}
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <LanguageSwitcher />
            <Link
              to="/cart"
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontSize: '0.85rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: location.pathname === '/cart' ? 'var(--orange)' : '#ffffff',
                textDecoration: 'none',
                transition: 'color 0.2s ease',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--orange)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = location.pathname === '/cart' ? 'var(--orange)' : '#ffffff'; }}
            >
              {t('nav.cart')}
              {cartCount > 0 && (
                <span style={{
                  background: 'var(--orange)',
                  color: '#fff',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  minWidth: '1.1rem',
                  height: '1.1rem',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 0.2rem',
                }}>
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>
            {user ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {user.profile_picture_url ? (
                    <img src={resolveMediaUrl(user.profile_picture_url)} alt="Avatar" loading="lazy" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border-hover)' }} />
                  ) : (
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--orange)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>
                      {firstName?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.82rem', color: '#ffffff', letterSpacing: '0.08em' }}>
                    {firstName || user.name}
                  </span>
                </div>
                <Link to="/profile" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.82rem', color: '#ffffff', textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('nav.profile')}</Link>
                {user.gender === 'female' && (
                  <Link to="/premium" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.82rem', color: '#D4AF37', textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    VIP
                    {vipStatus === 'active' && (
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#D4AF37', display: 'inline-block' }} />
                    )}
                    {vipStatus === 'pending' && (
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'orange', display: 'inline-block', animation: 'vip-pulse 1.5s ease-in-out infinite' }} />
                    )}
                  </Link>
                )}
                {(user.role === 'super_admin' || user.role === 'shop_admin') && (
                  <Link to="/admin" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.82rem', color: '#ffffff', textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('nav.admin')}</Link>
                )}
                {(user.role === 'shop_manager' || user.role === 'branch_manager') && (
                  <Link to="/manager" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.82rem', color: '#ffffff', textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t('nav.manager')}</Link>
                )}
                <button
                  onClick={handleLogout}
                  style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.82rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'none', border: '1px solid var(--border-hover)', borderRadius: '4px', color: '#ffffff', cursor: 'pointer', padding: '0.4rem 1rem', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.target.style.background = 'var(--orange)'; e.target.style.color = '#fff'; }}
                  onMouseLeave={e => { e.target.style.background = 'none'; e.target.style.color = '#ffffff'; }}
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <Link to="/login" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.85rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#ffffff', textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = 'var(--orange)'}
                onMouseLeave={e => e.target.style.color = '#ffffff'}
              >
                {t('nav.signIn')}
              </Link>
            )}
            <Link to={user ? "/shops" : "/login"} className="btn-orange" style={{ padding: '0.5rem 1.4rem', fontSize: '0.82rem' }}>
              {t('nav.bookNow')}
            </Link>
          </div>
        </div>

        {/* Mobile Nav Menu */}
        {
          mobileOpen && (
            <div id="mobile-nav-menu" style={{ background: 'var(--bg-card)', borderTop: '1px solid var(--border)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {navLinks.map(link => (
                <Link
                  key={link.label}
                  to={link.href}
                  style={{
                    fontFamily: "'Oswald', sans-serif",
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: location.pathname === link.href ? 'var(--orange)' : '#ffffff',
                    textDecoration: 'none',
                    textTransform: 'uppercase'
                  }}
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/cart"
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: location.pathname === '/cart' ? 'var(--orange)' : '#ffffff',
                  textDecoration: 'none',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
                onClick={() => setMobileOpen(false)}
              >
                {t('nav.cart')}
                {cartCount > 0 && (
                  <span style={{
                    background: 'var(--orange)',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    minWidth: '1.2rem',
                    height: '1.2rem',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>
              <div style={{ height: '1px', background: 'var(--border)' }} />
              {user ? (
                <>
                  <Link to="/profile" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', color: '#ffffff', textDecoration: 'none', textTransform: 'uppercase' }} onClick={() => setMobileOpen(false)}>{t('nav.profile')}</Link>
                  {user.gender === 'female' && (
                    <Link to="/premium" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', color: '#D4AF37', textDecoration: 'none', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={() => setMobileOpen(false)}>
                      VIP Premium
                      {vipStatus === 'active' && (
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#D4AF37', display: 'inline-block' }} />
                      )}
                      {vipStatus === 'pending' && (
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'orange', display: 'inline-block', animation: 'vip-pulse 1.5s ease-in-out infinite' }} />
                      )}
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', fontWeight: 600, background: 'none', border: 'none', color: '#ffffff', cursor: 'pointer', textAlign: 'left', padding: 0 }}
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link to="/login" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', color: '#ffffff', textDecoration: 'none', textTransform: 'uppercase' }} onClick={() => setMobileOpen(false)}>{t('nav.signIn')}</Link>
              )}
              <Link to="/privacy" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', color: '#ffffff', textDecoration: 'none', textTransform: 'uppercase' }} onClick={() => setMobileOpen(false)}>{t('nav.privacy')}</Link>
              <Link to="/terms" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', color: '#ffffff', textDecoration: 'none', textTransform: 'uppercase' }} onClick={() => setMobileOpen(false)}>{t('nav.terms')}</Link>
              <Link to="/contact" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1rem', color: '#ffffff', textDecoration: 'none', textTransform: 'uppercase' }} onClick={() => setMobileOpen(false)}>{t('nav.contact')}</Link>
              <Link to={user ? "/shops" : "/login"} className="btn-orange" style={{ textAlign: 'center' }} onClick={() => setMobileOpen(false)}>{t('nav.bookNow')}</Link>
            </div>
          )
        }
      </nav >

      {/* ── Main Content ── */}
      < main style={{ paddingTop: '4.5rem' }
      }>
        {children}
      </main >

      {/* ── Bottom orange bar ── */}
      < div className="orange-bar" />

      {/* ── Footer ── */}
      < footer style={{ background: 'var(--bg-footer)', padding: '4rem 1.5rem 2rem', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>

          {/* Top: logo + columns */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '3rem', marginBottom: '3rem' }}>

            {/* Brand */}
            <div>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2rem', letterSpacing: '0.12em', marginBottom: '0.5rem' }}>
                <span style={{ color: '#ffffff' }}>YALLA </span>
                <span style={{ color: 'var(--orange)', WebkitTextStroke: '2px var(--orange)', WebkitTextFillColor: 'transparent' }}>SALON</span>
              </div>
              <div className="orange-bar-sm" style={{ margin: '0 0 1rem' }} />
              <p style={{ fontSize: '0.85rem', color: '#ffffff', lineHeight: 1.7, maxWidth: '260px' }}>
                {t('footer.tagline')}
              </p>
              <div style={{ marginTop: '1.5rem' }}>
                <Link to={user ? "/shops" : "/login"} className="btn-orange" style={{ fontSize: '0.8rem', padding: '0.5rem 1.2rem' }}>{t('footer.bookNowArrow')}</Link>
              </div>
            </div>

            {/* Menu */}
            <div>
              <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '1rem' }}>{t('nav.menu')}</p>
              {[[t('nav.home'), '/'], [t('nav.salons'), '/shops'], [t('nav.products'), '/products'], [t('nav.signIn'), '/login'], [t('nav.signUp'), '/signup'], [t('nav.privacy'), '/privacy'], [t('nav.terms'), '/terms'], [t('nav.contact'), '/contact']].map(([label, href]) => (
                <p key={href} style={{ marginBottom: '0.5rem' }}>
                  <Link to={href} style={{ fontSize: '0.83rem', color: '#ffffff', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = 'var(--orange)'}
                    onMouseLeave={e => e.target.style.color = '#ffffff'}
                  >{label}</Link>
                </p>
              ))}
            </div>

            {/* Contact */}
            <div>
              <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '1rem' }}>{t('footer.contact')}</p>
              <p style={{ marginBottom: '0.5rem' }}>
                <a href={`mailto:${t('contact.email')}`} style={{ fontSize: '0.83rem', color: '#ffffff', textDecoration: 'none', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = 'var(--orange)'}
                  onMouseLeave={e => e.target.style.color = '#ffffff'}
                >{t('contact.email')}</a>
              </p>
              <p style={{ marginBottom: '0.5rem' }}>
                <a href={`tel:${t('contact.phone').replace(/\s/g, '')}`} style={{ fontSize: '0.83rem', color: '#ffffff', textDecoration: 'none', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = 'var(--orange)'}
                  onMouseLeave={e => e.target.style.color = '#ffffff'}
                >{t('contact.phone')}</a>
              </p>
            </div>

            {/* Social */}
            <div>
              <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '1rem' }}>{t('footer.social')}</p>
              {[
                { key: 'instagram', label: t('footer.instagram') },
                { key: 'facebook', label: t('footer.facebook') },
                { key: 'twitter', label: t('footer.twitter') },
                { key: 'tiktok', label: t('footer.tiktok') },
              ].map(({ key: k, label: s }) => (
                <p key={k} style={{ marginBottom: '0.5rem' }}>
                  <a href="#" style={{ fontSize: '0.83rem', color: '#ffffff', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = 'var(--orange)'}
                    onMouseLeave={e => e.target.style.color = '#ffffff'}
                  >{s}</a>
                </p>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="orange-bar" style={{ marginBottom: '1.5rem' }} />

          {/* Policy */}
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>{t('policy.noRefund')} {t('policy.noCancellation')}</p>

          {/* Bottom */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <p style={{ fontSize: '0.78rem', color: '#ffffff' }}>{t('footer.rights')}</p>
            <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '2rem', letterSpacing: '0.15em', color: 'var(--orange-glow)' }}>YALLA SALON</p>
          </div>
        </div>
      </footer >

      <style>{`
        @keyframes vip-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div >
  );
}
